import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { SelectedCurrencyService } from '@rx-applications/clientsite/shared/currency/data-access';
import { combineLatest, of, of as observableOf } from 'rxjs';
import { catchError, concatMap, delay, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { BaseConfig, EnvironmentConfiguration, UserConfiguration } from '@rx-applications/clientsite/shared/config/utils';
import { ENVIRONMENT_TOKEN, HttpService } from '@rx-applications/shared/utils';
import * as featureFlags from '@rx-applications/shared/clientsite/feature-flags';
import { FeatureFlagEnum, FeatureFlagFacade, FeatureFlagsState } from '@rx-applications/shared/clientsite/feature-flags';
import { InternationalizationService } from '@rx-applications/clientsite/shared/internationalization/data-access';
import { ConfigLoggerService } from '../services/logging/config-logger.service';
import { LoggingPropertyConfiguration } from '@rx-applications/clientsite/core/logging';
import { AuthApiFacade, AuthConfigApiModel, AuthConfigFacade } from '@rx-applications/shared/auth/api';
import { parseJWTToken, validateEmail } from '@rx-applications/clientsite/shared/utils';
import { AnalyticsConfigAPI, BaseConfigAPI, ClientConfigAPI, EnvConfigApi } from '../services';
import { ConfigFacade } from './config.facade';
import {
  analyticsConfig,
  baseConfigActions,
  cidActions,
  clientConfig,
  envConfigActions,
  initializeCurrentCurrency,
  initializeCurrentCurrencySucceed,
  localeActions,
  setSelectedCurrency,
  validateWebsite,
} from './config.actions';
import { CidStorageService } from '../services/cid-storage.service';
import { Branding } from '@rx-applications/shared/branding/utils';
import { BrandingManagerService } from '@rx-applications/shared/branding/api';
import { PageTitleService } from '../services/page-title.service';
import { ThemeService } from '../services/theme.service';
import { FaviconService } from '../services/favicon.service';
import { MetaTagsService } from '../services/meta-tags.service';

@Injectable()
export class ConfigEffects implements OnInitEffects {
  readonly #internationalizationService = inject(InternationalizationService);
  readonly #cidStorageService = inject(CidStorageService);
  readonly #appversion = inject(ENVIRONMENT_TOKEN).version;
  readonly #brandingManagerService = inject(BrandingManagerService);
  readonly #pageTitleService = inject(PageTitleService);
  readonly #themeService = inject(ThemeService);
  readonly #faviconService = inject(FaviconService);
  readonly #metaTagsService = inject(MetaTagsService);

  constructor(
    private actions$: Actions,
    private envConfigApi: EnvConfigApi,
    private baseConfigApi: BaseConfigAPI,
    private clientConfigAPI: ClientConfigAPI,
    private analyticsConfigAPI: AnalyticsConfigAPI,
    private http: HttpService,
    private configFacade: ConfigFacade,
    private configLoggerService: ConfigLoggerService,
    private selectedCurrencyService: SelectedCurrencyService,
    private authApiFacade: AuthApiFacade,
    private authConfigFacade: AuthConfigFacade,
    private featureFlagFacade: FeatureFlagFacade,
  ) {}

  initializeLocale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseConfigActions.loadBaseConfigSucceed),
      switchMap((loadBaseConfigSucceed) => {
        const useDefaultLocale = loadBaseConfigSucceed.baseConfig.features[featureFlags.FeatureFlagEnum.UseDefaultLocale];

        if (useDefaultLocale) {
          this.#internationalizationService.initLanguage(loadBaseConfigSucceed.baseConfig.defaultLocale);
          return observableOf(this.#internationalizationService.getActiveLanguage());
        } else {
          this.#internationalizationService.legacySetLanguage();
          return observableOf(this.#internationalizationService.legacyGetStoredLanguage());
        }
      }),
      map((newLocale) => localeActions.initializeLocaleSucceed({ newLocale })),
    ),
  );

  setPageTitle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(baseConfigActions.loadBaseConfigSucceed),
        tap(({ baseConfig }) => {
          this.#pageTitleService.setDefaultTitle();
        }),
      ),
    { dispatch: false },
  );

  setFavicon$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(baseConfigActions.loadBaseConfigSucceed),
        tap(() => {
          this.#faviconService.setFavicon();
        }),
      ),
    { dispatch: false },
  );

  setMetaColor$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(baseConfigActions.loadBaseConfigSucceed),
        tap(() => {
          this.#metaTagsService.setThemeMetaTag();
        }),
      ),
    { dispatch: false },
  );

  setTheme$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(baseConfigActions.loadBaseConfigSucceed),
        tap(({ baseConfig }) => {
          const useBrandConfigValue = baseConfig.features[featureFlags.FeatureFlagEnum.UseBrandConfigValue];
          if (useBrandConfigValue) {
            this.#themeService.setTheme();
          } else {
            this.#themeService.setTheme('roomex');
          }
        }),
      ),
    { dispatch: false },
  );

  changeLocale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(localeActions.changeLocale),
      map(({ newLocale }) => localeActions.changeLocaleSucceed({ newLocale })),
    ),
  );

  changeLocaleSucceed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(localeActions.changeLocaleSucceed),
        withLatestFrom(this.featureFlagFacade.useDefaultLocale$),
        tap(([{ newLocale }, useDefaultLocale]) => {
          if (useDefaultLocale) {
            this.#internationalizationService.setLanguage(newLocale);
          } else {
            this.#internationalizationService.legacySetLanguage(newLocale);
          }
        }),
        //Fix for https://roomex.atlassian.net/browse/STAY-786?focusedCommentId=60411
        delay(200),
        tap(() => location.reload()),
      ),
    { dispatch: false },
  );

  loadBaseConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseConfigActions.loadBaseConfig),
      switchMap(() =>
        this.baseConfigApi.getBaseConfig().pipe(
          tap((result: BaseConfig) => {
            if (result.websiteId === 0 || !result.forceLogin) {
              location.href = 'https://www.roomex.com';
            }

            const useBrandConfigValue = result.features[featureFlags.FeatureFlagEnum.UseBrandConfigValue];
            const branding = useBrandConfigValue ? result.brandConfig : Branding.RoomexBrand;
            this.#brandingManagerService.setBranding(branding);

            this.authConfigFacade.setAuthConfig(result as AuthConfigApiModel);
            this.configLoggerService.setProperties({ websiteId: result.websiteId });
          }),
          map((baseConfig: BaseConfig) => envConfigActions.prepareEnvironmentRxwebapi({ baseConfig })),
          catchError((error) => observableOf(baseConfigActions.loadBaseConfigFailed({ error }))),
        ),
      ),
    ),
  );

  prepareEnvironmentRxwebapi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(envConfigActions.prepareEnvironmentRxwebapi),
      withLatestFrom(this.configFacade.environmentConfiguration$),
      map(([{baseConfig}, environmentConfiguration]) => {
        const enableLodgxApiDomain = baseConfig.features[featureFlags.FeatureFlagEnum.EnableLodgxApiDomain];
        if (enableLodgxApiDomain) {
          const lodgxApiUrl = this.#mapEnvironmentNameToLodgxUrl(environmentConfiguration.env, environmentConfiguration.rxwebapi);
          this.http.webApiUrl = lodgxApiUrl;
          return envConfigActions.updateEnvironmentRxwebapi({ rxwebapi: lodgxApiUrl, baseConfig });
        }
        return baseConfigActions.loadBaseConfigSucceed({ baseConfig });
      }),
    ),
  );

  updateEnvironmentRxwebapi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(envConfigActions.updateEnvironmentRxwebapi),
      map(({ rxwebapi, baseConfig }) => {
        return baseConfigActions.loadBaseConfigSucceed({ baseConfig });
      }),
    ),
  );

  loadBaseConfigSucceed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(baseConfigActions.loadBaseConfigSucceed),
        withLatestFrom(this.configFacade.environmentConfiguration$),
        tap(([{ baseConfig }, envConfig]) => {
          const features = baseConfig.features;
          const recordSession = features[FeatureFlagEnum.ReplaySession];
          const loggingPropertyConfiguration: LoggingPropertyConfiguration = {
            abTest: features,
            recordSession,
            brand: baseConfig.brandConfig,
            tenantId: baseConfig.tenantId,
            websiteId: baseConfig.websiteId,
          };
          this.configLoggerService.setProperties(loggingPropertyConfiguration);
          this.configLoggerService.init(envConfig?.tracking, baseConfig);
          this.featureFlagFacade.setFlags(<FeatureFlagsState>features);
        }),
      ),
    { dispatch: false },
  );

  validateWebsiteId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(validateWebsite),
      withLatestFrom(this.configFacade.websiteId$.pipe(filter((value) => !!value))),
      filter(([_, websiteId]) => {
        const tokenWebsiteId = this.authApiFacade.getTokenWebsiteId();
        return !!tokenWebsiteId && websiteId !== tokenWebsiteId;
      }),
      tap(() => this.configLoggerService.UserTokenNotValidForThisPortal()),
      map(() => this.authApiFacade.handleIncorrectPortal({ email: this.authApiFacade.getTokenEmail() })),
    ),
  );

  onLoginAndBaseConfigLoadSucceed$ = createEffect(() =>
    combineLatest([this.authApiFacade.isAuthenticated$, this.configFacade.hasBaseConfigurationHttpCompleted$]).pipe(
      filter(([isAuthenticated, isBaseConfigurationLoaded]) => isAuthenticated && isBaseConfigurationLoaded),
      switchMap(() => [validateWebsite(), clientConfig.load()]),
    ),
  );

  loadUserConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clientConfig.load),
      withLatestFrom(
        this.configFacade.environmentConfiguration$,
        this.authApiFacade.token$,
        this.#internationalizationService.getLanguage(),
        (_, envConfig, token, language) => ({
          envConfig,
          token,
          language,
        }),
      ),
      switchMap(({ envConfig, token, language }) =>
        this.clientConfigAPI.getClientConfig().pipe(
          mergeMap((user) => {
            const { configuration } = user;
            try {
              const email = configuration.general.emailAddress
                ? configuration.general.emailAddress
                : this.getUsernameFromToken(<string>token ?? '');
              const name = configuration.general.fullName
                ? configuration.general.fullName
                : `${configuration.general.firstName} ${configuration.general.lastName}`;

              this.configLoggerService.setProperties({
                username: { email, name },
                company: configuration.general.company.name,
                version: this.#appversion,
                role: this.authApiFacade.getTokenRole(),
              });

              this.setUpIntercom(configuration, envConfig, language);
            } catch (e) {
              throw new Error(e);
            }
            return [
              clientConfig.loadSuccess({ configuration }),
              initializeCurrentCurrency({
                payload: configuration.general.defaultCurrency,
              }),
            ];
          }),
          catchError((error) => of(clientConfig.loadFailed({ error }))),
        ),
      ),
    ),
  );

  loadEnvConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(envConfigActions.loadEnvironment),
      switchMap(() =>
        this.envConfigApi.getEnvConfig().pipe(
          map((result: EnvironmentConfiguration) =>
            envConfigActions.loadEnvironmentSucceed({ environmentConfiguration: result }),
          ),
          catchError((error) => observableOf(envConfigActions.loadEnvironmentFailed({ error }))),
        ),
      ),
    ),
  );

  loadEnvConfigSucceed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(envConfigActions.loadEnvironmentSucceed),
        tap(({ environmentConfiguration }) => {
          this.http.authApiUrl = environmentConfiguration.authserver;
          this.http.webApiUrl = environmentConfiguration.rxwebapi;
          this.http.payApiUrl = environmentConfiguration.payapi;
        }),
      ),
    { dispatch: false },
  );

  generateCidSucceed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cidActions.generateCidSucceed, cidActions.readCidSucceed),
        tap(({ guid }) => {
          this.configLoggerService.setProperties({ cid: guid });
        }),
      ),
    { dispatch: false },
  );

  loadReportingConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(analyticsConfig.load),
      concatMap(() =>
        this.analyticsConfigAPI.getAnalyticsConfig().pipe(
          map(({ configuration }) => analyticsConfig.loadSuccess({ reportingConfiguration: configuration.reports })),
          catchError((error) => of(analyticsConfig.loadFailed(error))),
        ),
      ),
    ),
  );

  initializeCurrentCurrency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initializeCurrentCurrency),
      switchMap(({ payload: defaultCurrency }) => {
        let persisted = this.selectedCurrencyService.getSelectedCurrency();
        if (persisted.length) {
          persisted = persisted.replace(/currencyCode=/, '');
        } else {
          persisted = defaultCurrency;
          this.selectedCurrencyService.setSelectedCurrency(defaultCurrency);
        }

        return of(initializeCurrentCurrencySucceed({ payload: persisted }));
      }),
    ),
  );

  setCurrentCurrency$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setSelectedCurrency),
        tap(({ payload: selectedCurrency }) => {
          this.selectedCurrencyService.setSelectedCurrency(selectedCurrency);
        }),
      ),
    { dispatch: false },
  );

  ngrxOnInitEffects() {
    return envConfigActions.loadEnvironment();
  }

  private setUpIntercom(userConfig: UserConfiguration, envConfig: EnvironmentConfiguration, language: string = 'en'): void {
    const { intercom } = envConfig.tracking;
    this.configLoggerService.setProperties({
      userProperties: {
        app_id: intercom.key,
        email: userConfig.general.emailAddress,
        created_at: userConfig.general.userCreatedAt,
        name: userConfig.general.fullName || `${userConfig.general.company.name} User`,
        user_hash: userConfig.general.userHash,
        company: {
          id: userConfig.general.websiteId,
          website: location.hostname,
          name: userConfig.general.company.name,
          plan: userConfig.general.company.plan,
          monthly_spend: userConfig.general.company.monthlySpend,
          created_at: userConfig.general.company.companyCreatedAt,
        },
        language_override: language,
      },
    });
  }

  private getUsernameFromToken(token: string) {
    const claims = parseJWTToken(token);

    if (claims && claims.unique_name && claims.unique_name.length > 0) {
      if (validateEmail(claims.unique_name[0])) {
        return claims.unique_name[0];
      }
      if (validateEmail(claims.unique_name[1])) {
        return claims.unique_name[1];
      }
    }

    return '';
  }

  #mapEnvironmentNameToLodgxUrl(env: string, defaultApiUrl): string {
    const envMappings: Record<string, string> = {
      development: 'https://staypi.dev.lodgx.io/',
      staging: 'https://staypi.stg.lodgx.io/',
      prodv2: 'https://staypi.lodgx.io/'
    };

    const normalizedEnv = env.toLowerCase();
    return envMappings[normalizedEnv] || defaultApiUrl;
  }
}
